// Session Editor side panels (design §7b, §7c, §7a UI): rename with Sonra labels, timed blocks, suggested periods,
// Match Day cut, timeline notes, unit status, drill library, hint thresholds and history, keyboard shortcuts, and on
// match days the match-periods list and "count this stretch" (gaps build Stage B).
import React, { useEffect, useState } from 'react';
import {
  getPeriodSuggestions, createSuggestedPeriods, getMatchdayCut, cutFromMatchday, previewPeriodBlocks, setPeriodBlocks, removePeriodBlocks,
  getPeriodLibrary, getPeriodHintSettings, savePeriodHintSettings, getMatchPeriodList, saveMatchPeriodList,
} from '../../../api';
import { composeName, clock, toMs, parseClock, mmss, fmtNum, isMatchLabels } from './seShared';

const Err = ({ msg }) => (msg ? <div className="se-error" style={{ marginTop: 6 }}>{msg}</div> : null);

// ── Rename (§7b): Sonra labels (primary → its secondaries → tertiary → free text) or an own name. Part of the draft. ──
export function RenamePanel({ vocab, draft, onApply, onClose }) {
  const [mode, setMode] = useState(draft.nameSource === 'labels' ? 'labels' : 'custom');
  const [l, setL] = useState({ primary: '', secondary: '', tertiary: '', free: '', ...Object.fromEntries(Object.entries(draft.labels || {}).map(([k, v]) => [k, v || ''])) });
  const [own, setOwn] = useState(draft.nameSource === 'labels' ? '' : draft.name);
  const prim = (vocab && vocab.primary) || [];
  const p = prim.find(x => x.value === l.primary);
  const labels = { primary: l.primary || null, secondary: l.secondary || null, tertiary: l.tertiary || null, free: l.free || null };
  const name = mode === 'labels' ? composeName(labels) : own.trim();
  return (
    <div className="se-sub">
      <div className="se-bar">
        <label><input type="radio" checked={mode === 'labels'} onChange={() => setMode('labels')} /> Sonra labels</label>
        <label><input type="radio" checked={mode === 'custom'} onChange={() => setMode('custom')} /> own name</label>
      </div>
      <div className="se-bar" style={{ marginTop: 6 }}>
        <select className="se-input" aria-label="Primary label" value={l.primary} onChange={e => setL({ ...l, primary: e.target.value, secondary: '' })}>
          <option value="">primary label…</option>
          {prim.map(x => <option key={x.value} value={x.value}>{x.value.trim()} ({x.count})</option>)}
          {l.primary && !p && <option value={l.primary}>{l.primary}</option>}
        </select>
        <select className="se-input" aria-label="Secondary label" value={l.secondary} onChange={e => setL({ ...l, secondary: e.target.value })}>
          <option value="">secondary…</option>
          {(p ? p.secondary : []).map(x => <option key={x.value} value={x.value}>{x.value.trim()} ({x.count})</option>)}
          {l.secondary && !(p && p.secondary.some(x => x.value === l.secondary)) && <option value={l.secondary}>{l.secondary}</option>}
        </select>
        <select className="se-input" aria-label="Tertiary label" value={l.tertiary} onChange={e => setL({ ...l, tertiary: e.target.value })}>
          <option value="">tertiary…</option>
          {((vocab && vocab.tertiary) || []).map(x => <option key={x.value} value={x.value}>{x.value}</option>)}
        </select>
        <input className="se-input" list="se-free" placeholder="free text (optional)" aria-label="Free text" value={l.free} onChange={e => setL({ ...l, free: e.target.value })} />
        <datalist id="se-free">{((vocab && vocab.freeText) || []).map(x => <option key={x.value} value={x.value} />)}</datalist>
      </div>
      {mode === 'custom' && <div className="se-bar" style={{ marginTop: 6 }}><input className="se-input" style={{ width: 300 }} placeholder="period name" aria-label="Own name" value={own} onChange={e => setOwn(e.target.value)} /></div>}
      <div className="se-bar" style={{ marginTop: 8 }}>
        <span className="se-muted">Name:</span> <strong>{name || '–'}</strong>
        {mode === 'custom' && <span className="se-muted">(labels stay on the period, so it still groups with the same Sonra drills)</span>}
        <button className="se-btn se-btn--primary" disabled={!name} onClick={() => onApply({ name, labels, nameSource: mode })}>Apply</button>
        <button className="se-btn" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

// ── Timed blocks (§7c) ───────────────────────────────────────────────────────────────────────────────────────────
const PRESETS = [['h1', '1st half'], ['h2', '2nd half'], ['e1', 'Extra time, 1st half'], ['e2', 'Extra time, 2nd half'], ['drill', 'Drill']];
export function BlocksPanel({ token, period, dirty, onDone, onClose }) {
  const def = !isMatchLabels(period.labels) ? 'drill' : period.labels.secondary === 'Second Half' ? 'h2' : 'h1';
  const [minutes, setMinutes] = useState(period.generator ? period.generator.minutes : 5);
  const [preset, setPreset] = useState(period.generator ? period.generator.preset : def);
  const [plan, setPlan] = useState(null); const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    previewPeriodBlocks(token, period.id, minutes, preset).then(r => live && setPlan(r.blocks)).catch(e => live && setErr(e.message));
    return () => { live = false; };
  }, [token, period.id, minutes, preset]);
  const run = fn => fn().then(onDone).catch(e => setErr(e.message));
  return (
    <div className="se-sub">
      <div className="se-bar">
        <strong>Split into timed blocks</strong>
        <label className="se-muted">Block <select className="se-input" value={minutes} onChange={e => setMinutes(Number(e.target.value))}>{[1, 2, 3, 5, 10, 15].map(m => <option key={m} value={m}>{m} min</option>)}</select></label>
        <label className="se-muted">Count as <select className="se-input" value={preset} onChange={e => setPreset(e.target.value)}>{PRESETS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
      </div>
      <div className="se-muted" style={{ marginTop: 6 }}>
        {plan ? plan.map(b => `${b.name} (${mmss((toMs(b.end) - toMs(b.start)) / 1000)})`).join(' · ') : '…'}
      </div>
      <div className="se-muted" style={{ marginTop: 4 }}>Blocks are read-only and follow this period: saving a new window makes them again. The + block runs to the whistle.</div>
      {dirty && <div className="se-banner" style={{ marginTop: 6 }}>Save or cancel your changes first.</div>}
      <div className="se-bar" style={{ marginTop: 8 }}>
        <button className="se-btn se-btn--primary" disabled={dirty || !plan} onClick={() => run(() => setPeriodBlocks(token, period.id, { version: period.version, minutes, preset }))}>
          {period.generator ? 'Replace with' : 'Create'} {plan ? plan.length : ''} blocks
        </button>
        {period.generator && <button className="se-btn" disabled={dirty} onClick={() => run(() => removePeriodBlocks(token, period.id, period.version))}>Remove blocks</button>}
        <button className="se-btn" onClick={onClose}>Close</button>
      </div>
      <Err msg={err} />
    </div>
  );
}

// ── Suggested periods (§7c): Entire Session per player, Sonra's drills first, pause detection as a fallback ──────────
export function SuggestPanel({ token, sessionId, labelOf, onDone, onClose }) {
  const [data, setData] = useState(null); const [pick, setPick] = useState({}); const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    getPeriodSuggestions(token, sessionId).then(r => {
      if (!live) return;
      setData(r);
      const all = [r.entireSession, ...r.drills];
      setPick(Object.fromEntries(all.map((x, i) => [i, !!x.pick])));
    }).catch(e => live && setErr(e.message));
    return () => { live = false; };
  }, [token, sessionId]);
  if (!data) return <div className="se-sub">{err ? <Err msg={err} /> : <span className="se-muted">Finding periods…</span>}</div>;
  const items = [data.entireSession, ...data.drills];
  const add = () => {
    setBusy(true);
    const chosen = items.filter((_, i) => pick[i]).map(x => ({ kind: x.kind, name: x.name, labels: x.labels, start: x.start, end: x.end, players: x.players, ownWindows: x.ownWindows, groups: x.groups }));
    createSuggestedPeriods(token, { sessionId, items: chosen }).then(onDone).catch(e => { setErr(e.message); setBusy(false); });
  };
  return (
    <div className="se-sub">
      <div className="se-bar" style={{ justifyContent: 'space-between' }}>
        <strong>Suggested periods</strong>
        <span className="se-muted">window: {data.block.source === 'planner' ? `Planner block ${data.block.title || ''}` : 'the StatSports session'} {clock(toMs(data.block.start)).slice(0, 5)}–{clock(toMs(data.block.end)).slice(0, 5)}{data.plannerGroups ? ` · ${data.plannerGroups} players in Planner groups` : ''}</span>
      </div>
      <table className="se-table" style={{ marginTop: 6 }}>
        <tbody>
          {items.map((x, i) => (
            <tr key={`${x.kind}-${i}`}>
              <td className="l"><input type="checkbox" checked={!!pick[i]} onChange={() => setPick({ ...pick, [i]: !pick[i] })} aria-label={`add ${x.name}`} /></td>
              <td className="l"><strong>{x.name}</strong>{x.already && <span className="se-pill" style={{ marginLeft: 6 }}>already cut</span>}{x.lowIntensity && <span className="se-pill" style={{ marginLeft: 6 }}>low intensity {x.dpm} m/min</span>}</td>
              <td className="l">{clock(toMs(x.start))}–{clock(toMs(x.end))}</td>
              <td className="l" style={{ whiteSpace: 'normal' }}>
                <span className="se-muted">{x.nameFrom}</span>
                {x.groups && <div className="se-muted">split: group 1 {clock(toMs(x.groups[1].start))}–{clock(toMs(x.groups[1].end))}, group 2 {clock(toMs(x.groups[2].start))}–{clock(toMs(x.groups[2].end))}</div>}
                {x.kind === 'entire_session' && x.players.filter(p => p.note).map(p => <div key={p.sessionPlayerId} className="se-muted">{labelOf(p.sessionPlayerId)}: own window · auto: {p.note}</div>)}
                {(x.ownWindows || []).length > 0 && <div className="se-muted">{x.ownWindows.length} own window{x.ownWindows.length > 1 ? 's' : ''} (Sonra's own cut differs ≥ 10 s)</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="se-bar" style={{ marginTop: 8 }}>
        <button className="se-btn se-btn--primary" disabled={busy || !Object.values(pick).some(Boolean)} onClick={add}>Add ticked as my periods</button>
        <button className="se-btn" onClick={onClose}>Close</button>
      </div>
      <Err msg={err} />
    </div>
  );
}

// ── Cut a match from Match Day (§7c; decisions 2026-10-29: lineup players without GPS who played stay in, as
// "no GPS"; the clock offset is a suggestion with the moment it was found, applied only when you confirm or type it) ──
export function MatchdayPanel({ token, sessionId, labelOf, labelOfPlayer, onDone, onClose }) {
  const [offset, setOffset] = useState(0); const [typed, setTyped] = useState('0');
  const [data, setData] = useState(null); const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => getMatchdayCut(token, sessionId, offset).then(r => { if (live) { setData(r); setErr(''); } }).catch(e => live && setErr(e.message)), 200);
    return () => { live = false; clearTimeout(t); };
  }, [token, sessionId, offset]);
  const applyTyped = v => {
    setTyped(v);
    const n = Number(String(v).replace(',', '.'));
    if (String(v).trim() !== '' && Number.isFinite(n) && Math.abs(n) <= 600) { setOffset(Math.round(n * 10) / 10); setErr(''); } else setErr('offset in seconds, between −600 and +600');
  };
  const sign = x => `${x > 0 ? '+' : ''}${x}`;
  const cut = () => {
    setBusy(true);
    cutFromMatchday(token, sessionId, { fixtureId: data.fixture.id, clockOffsetSeconds: offset }).then(onDone).catch(e => { setErr(e.message); setBusy(false); });
  };
  return (
    <div className="se-sub">
      <div className="se-bar"><strong>Cut from Match Day</strong>{data && <span className="se-muted">{data.fixture.homeAway === 'H' ? 'vs' : 'at'} {data.fixture.opponent} · squad {data.squad} · {data.subs} substitution{data.subs === 1 ? '' : 's'}</span>}</div>
      {data && (
        <>
          <div className="se-bar" style={{ marginTop: 6 }}>
            <span className="se-muted">Match Day kick-off {clock(toMs(data.kickoffMatchday))} (phone clock).</span>
            {data.suggestedOffsetSeconds == null
              ? <span>Suggestion: no clear kick-off <span className="se-muted">(no 10 unbroken seconds of most starters moving within ± {data.suggestionWindowSeconds} s of the Match Day kick-off)</span></span>
              : (
                <span>
                  Suggestion: <strong>{sign(data.suggestedOffsetSeconds)} s</strong> (the starters with GPS start moving at {clock(toMs(data.suggestedOffsetAt))})
                  <button className="se-btn se-btn--small" style={{ marginLeft: 6 }} disabled={offset === data.suggestedOffsetSeconds}
                    onClick={() => applyTyped(String(data.suggestedOffsetSeconds))}>Use it</button>
                </span>
              )}
          </div>
          <div className="se-bar" style={{ marginTop: 6 }}>
            <label className="se-muted">Offset applied to every Match Day time
              <input className="se-input" style={{ width: 70, marginLeft: 6 }} aria-label="Clock offset in seconds" value={typed} onChange={e => applyTyped(e.target.value)} /> s</label>
            <span className="se-muted">{offset === 0 ? 'none applied (the halves use the Match Day clock as it is)' : `applied: ${sign(offset)} s`}</span>
            {offset !== 0 && <button className="se-btn se-btn--small" onClick={() => applyTyped('0')}>Reset to 0</button>}
          </div>
          {data.plan.halves.map(h => (
            <div key={h.name} style={{ marginTop: 6 }}>
              <strong>{h.name}</strong> <span className="se-muted">{clock(toMs(h.start))}–{clock(toMs(h.end))} · {h.players.filter(p => p.included).length} with GPS</span>
              {h.players.filter(p => p.included && (p.start || p.end)).map(p => <div key={p.sessionPlayerId} className="se-muted">{labelOf(p.sessionPlayerId)}: {p.start ? `on ${clock(toMs(p.start))}` : ''}{p.start && p.end ? ', ' : ''}{p.end ? `off ${clock(toMs(p.end))}` : ''}</div>)}
              {(h.noGps || []).map(p => <div key={p.playerId} className="se-muted">{labelOfPlayer(p.playerId)}: on the pitch, <span className="se-pill">no GPS</span></div>)}
            </div>
          ))}
        </>
      )}
      <div className="se-bar" style={{ marginTop: 8 }}>
        <button className="se-btn se-btn--primary" disabled={!data || busy || data.appliedOffsetSeconds !== offset} onClick={cut}>Create both halves{offset ? ` (offset ${sign(offset)} s)` : ''}</button>
        <button className="se-btn" onClick={onClose}>Close</button>
      </div>
      <Err msg={err} />
    </div>
  );
}

// ── Timeline notes (§7c): about one player or everyone, saved straight away, admins only, factual ──────────────────
export function NotesPanel({ notes, players, labelOf, refMs, defaultAt, onAdd, onDelete, onGo, open, setOpen }) {
  const [time, setTime] = useState(''); const [who, setWho] = useState(''); const [text, setText] = useState(''); const [err, setErr] = useState('');
  useEffect(() => { if (open) { setTime(clock(defaultAt != null ? defaultAt : refMs)); setErr(''); } }, [open, defaultAt, refMs]);
  const add = () => {
    const at = parseClock(time, refMs);
    if (at == null) { setErr('time as hh:mm:ss'); return; }
    if (!text.trim()) { setErr('write what happened'); return; }
    onAdd({ at: new Date(at).toISOString(), sessionPlayerId: who || null, text: text.trim() }).then(() => { setText(''); setOpen(false); }).catch(e => setErr(e.message));
  };
  return (
    <>
      <div className="se-bar" style={{ marginTop: 8 }}>
        <button className="se-btn" onClick={() => setOpen(!open)}>＋ Note</button>
        <span className="se-muted">at the mouse position (key M) · on the timeline · admins only</span>
      </div>
      {open && (
        <div className="se-sub">
          <div className="se-bar">
            <input className="se-input se-time" aria-label="Note time" value={time} onChange={e => setTime(e.target.value)} />
            <select className="se-input" aria-label="Note about" value={who} onChange={e => setWho(e.target.value)}>
              <option value="">everyone</option>
              {players.map(p => <option key={p} value={p}>{labelOf(p)}</option>)}
            </select>
            <input className="se-input" style={{ flex: 1, minWidth: 220 }} maxLength={500} placeholder="what happened" aria-label="Note text" value={text} onChange={e => setText(e.target.value)} />
            <button className="se-btn se-btn--primary" onClick={add}>Add</button>
            <button className="se-btn" onClick={() => setOpen(false)}>Close</button>
          </div>
          <div className="se-muted" style={{ marginTop: 4 }}>Notes belong to the session and are saved straight away. Admins only, never visible to players. Keep them factual: no diagnoses.</div>
          <Err msg={err} />
        </div>
      )}
      {notes.length > 0 && (
        <ul className="se-notes">
          {notes.map(n => (
            <li key={n.id}>
              <span style={{ fontFamily: 'ui-monospace, monospace' }}>{clock(toMs(n.at))}</span>
              <span className="se-muted">{n.sessionPlayerId ? labelOf(n.sessionPlayerId) : 'everyone'}</span>
              <span style={{ flex: 1 }}>{n.text}</span>
              <button className="se-btn se-btn--small" onClick={() => onGo(n)}>Go</button>
              <button className="se-btn se-btn--small" onClick={() => onDelete(n)} aria-label="Delete note">✕</button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Units in this session (§7c): checked against the Planner block; click jumps to the player and moment ─────────
const UNIT_TEXT = { no_data: () => 'no data', late_start: i => `starts ${mmss(i.seconds)} late`, early_stop: i => `stopped ${mmss(i.seconds)} early (battery or switched off?)`,
  gap: i => `gap ${mmss(i.seconds)} at ${clock(toMs(i.from))}`, no_position: i => `no position ${mmss(i.seconds)} (longest ${mmss(i.longest)})` };
// Checked against Entire Session - Live (decision 2026-10-29): mine (each player against his own window), else Sonra's,
// else the span of the drills; with no drills at all there is nothing to check yet.
const span = r => `${clock(toMs(r.start)).slice(0, 5)}–${clock(toMs(r.end)).slice(0, 5)}`;
const REF_TEXT = {
  mine: r => `checked against your Entire Session - Live ${span(r)}, each player against his own window`,
  sonra: r => `no Entire Session - Live of yours yet: checked against Sonra's Entire Session - Live ${span(r)}, each player against his own drill window`,
  drills: r => `no Entire Session - Live yet: checked against the drills ${span(r)} (first drill start to last drill end)`,
};
export function UnitsStrip({ units, labelOf, onJump }) {
  if (!units) return <span className="se-muted">…</span>;
  const ref = units.reference || { kind: 'drills', start: units.block && units.block.start, end: units.block && units.block.end };
  if (ref.kind === 'none') return <div className="se-muted">No drills yet: the unit check waits for Entire Session - Live.</div>;
  return (
    <>
      <div className="se-muted">{units.recorded} of {units.players} units recorded · {REF_TEXT[ref.kind](ref)} · {Math.round(units.noPositionShare * 100)} % of seconds without position</div>
      <div className="se-bar" style={{ marginTop: 6 }}>
        {units.items.length === 0 && <span className="se-pill">all units fine</span>}
        {units.items.slice(0, 40).map((i, k) => (
          <button key={k} className={`se-pill ${i.severity === 'bad' ? 'se-pill--bad' : i.severity === 'warn' ? 'se-pill--warn' : ''}`} style={{ border: 'none', cursor: 'pointer' }} onClick={() => onJump(i)}>
            {labelOf(i.sessionPlayerId)}: {(UNIT_TEXT[i.kind] || (() => i.kind))(i)}
          </button>
        ))}
      </div>
    </>
  );
}

// ── Drill library (§7c): this drill's squad mean next to earlier drills with the same labels (8 weeks) ──────────
const LIB = [['dpm', 'm/min', s => s.distPerMin], ['hsrpm', 'HSR m/min', s => (s.minutes ? s.hsr / s.minutes : null)], ['z6pm', 'sprint m/min', s => (s.minutes ? s.sprint / s.minutes : null)], ['pk1', 'peak 1′', s => s.peak60]];
export function LibraryBox({ token, labels, squadMean }) {
  const [lib, setLib] = useState(null);
  const primary = labels && labels.primary; const secondary = labels && labels.secondary;
  useEffect(() => {
    let live = true; setLib(null);
    if (primary) getPeriodLibrary(token, primary, secondary).then(r => live && setLib(r)).catch(() => live && setLib(null));
    return () => { live = false; };
  }, [token, primary, secondary]);
  if (!primary || !lib) return null;
  if (!lib.typical) return <div className="se-muted" style={{ marginBottom: 6 }}>Drill library · {[primary, secondary].filter(Boolean).join('-')}: fewer than 3 earlier drills in {lib.weeks} weeks.</div>;
  return (
    <div className="se-sub" style={{ marginBottom: 8 }}>
      <div className="se-bar"><strong>Drill library · {[primary, secondary].filter(Boolean).map(x => x.trim()).join('-')}</strong><span className="se-muted">{lib.drills} earlier drills, last {lib.weeks} weeks (squad means; outfield, not modified)</span></div>
      <div className="se-bar" style={{ marginTop: 6 }}>
        {LIB.map(([k, label, get]) => {
          const t = lib.typical[k]; const mine = squadMean ? get(squadMean) : null;
          if (!t) return null;
          const where = mine == null ? '' : mine < t.p25 ? 'below' : mine > t.p75 ? 'above' : 'typical';
          return (
            <span key={k} className={`se-pill ${where === 'typical' || !where ? '' : 'se-pill--warn'}`}>
              {label}: {mine == null ? '–' : fmtNum(mine, 0)} {where && `(${where})`} · typical {fmtNum(t.p25, 0)} – {fmtNum(t.median, 0)} – {fmtNum(t.p75, 0)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── Hint thresholds (admins; versioned) and history ──────────────────────────────────────────────────────────────
const TH_LABELS = [['edgeSec', 'recording starts late / ends early by ≥ s'], ['noGpsSec', 'no-GPS stretch at an edge ≥ s'], ['idleSpeed', 'standing still: 5-s mean below m/s'],
  ['idleSec', 'standing still for ≥ s'], ['squadMoving', 'squad moving: median ≥ m/s'], ['squadShare', '… for this share of the stretch'], ['inactiveDpm', 'inactive below m/min'],
  ['squadDpm', '… while the squad does ≥ m/min'], ['notInCoverage', 'not in period: data for ≥ share of the window'], ['notInShare', '… and ≥ share of squad m/min'],
  ['dismissTol', 'a dismissed hint stays dismissed within ± s'], ['minRef', 'minimum reference players'], ['insideSec', 'went inside: ≥ s at the end'],
  ['spikeSpeed', 'GPS spike: run ≥ m/s'], ['spikeMaxSec', '… lasting ≤ s'], ['spikeAccel', '… with a jump ≥ m/s²'], ['spikeCap', 'any sample above m/s'],
  ['posPathRatio', 'poor position: path ÷ speed distance ≥'], ['posSpreadM', 'poor position: spread p90 ≥ m'], ['posOffPitchShare', 'poor position: off-pitch share ≥ (match days)'],
  ['gapMoveMinKmh', 'movement in an excluded stretch: clean run ≥ km/h (match days)'], ['gapMoveTolKmh', '… and its speed + this km/h above his halves/runs max'],
  ['gapMovePosLo', '… and its positions ÷ its speed at least'], ['gapMovePosHi', '… and at most']];
// canEdit: admins only (the server refuses a save from anyone else); others see the thresholds read-only.
export function ThresholdsPanel({ token, onSaved, canEdit = true }) {
  const [s, setS] = useState(null); const [edit, setEdit] = useState({}); const [err, setErr] = useState('');
  useEffect(() => { getPeriodHintSettings(token).then(setS).catch(e => setErr(e.message)); }, [token]);
  if (!s) return <div className="se-sub">{err ? <Err msg={err} /> : '…'}</div>;
  const save = () => savePeriodHintSettings(token, { version: s.version, thresholds: edit }).then(r => { setS({ ...s, ...r }); setEdit({}); onSaved(); }).catch(e => setErr(e.message));
  return (
    <div className="se-sub">
      <div className="se-bar"><strong>Hint thresholds</strong><span className="se-muted">version {s.version} · {canEdit ? 'admins only' : 'read-only: only admins change these'} · stored with every decision</span></div>
      <table className="se-keys" style={{ marginTop: 6 }}>
        <tbody>
          {TH_LABELS.filter(([k]) => s.thresholds[k] !== undefined).map(([k, label]) => (
            <tr key={k}>
              <td>{label}</td>
              <td><input className="se-input" style={{ width: 70 }} value={edit[k] !== undefined ? edit[k] : s.thresholds[k]} onChange={e => setEdit({ ...edit, [k]: e.target.value })} aria-label={label} disabled={!canEdit} /></td>
              <td className="se-muted">default {String(s.defaults[k])}</td>
            </tr>
          ))}
          <tr><td>idle and inactive hints for goalkeepers</td><td><input type="checkbox" checked={edit.gk !== undefined ? !!edit.gk : !!s.thresholds.gk} onChange={e => setEdit({ ...edit, gk: e.target.checked })} disabled={!canEdit} /></td><td className="se-muted">default off</td></tr>
        </tbody>
      </table>
      {canEdit && <div className="se-bar" style={{ marginTop: 6 }}><button className="se-btn se-btn--primary" disabled={!Object.keys(edit).length} onClick={save}>Save thresholds</button></div>}
      <Err msg={err} />
    </div>
  );
}
// ── Match-periods list (gaps build Stage B): which drill names count in a match session. Admins add, rename, remove and
// classify entries; editors read it. An entry matches an exact drill name (what you add here) or a pattern (the
// defaults); the first match wins. unknown: this session's drills with unknown names, each with "add as …". ─────────
export const MATCH_CLASSES = [['max', 'max speed and load'], ['load', 'load only'], ['none', 'neither']];
export function MatchListPanel({ token, canEdit, unknown = [], onSaved }) {
  const [s, setS] = useState(null); const [rows, setRows] = useState(null); const [err, setErr] = useState(''); const [add, setAdd] = useState({ name: '', cls: 'max' });
  useEffect(() => { getMatchPeriodList(token).then(r => { setS(r); setRows(r.entries.map(e => ({ ...e }))); }).catch(e => setErr(e.message)); }, [token]);
  if (!s || !rows) return <div className="se-sub">{err ? <Err msg={err} /> : '…'}</div>;
  const dirty = JSON.stringify(rows) !== JSON.stringify(s.entries);
  const done = r => { setS(r); setRows(r.entries.map(e => ({ ...e }))); setErr(''); onSaved && onSaved(); };
  const save = () => saveMatchPeriodList(token, { version: s.version, entries: rows }).then(done).catch(e => setErr(e.message));
  const reset = () => window.confirm('Back to the default list? Your own entries are removed.') && saveMatchPeriodList(token, { version: s.version, reset: true }).then(done).catch(e => setErr(e.message));
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const names = [...new Set(unknown.map(u => u.name))].filter(n => !rows.some(r => r.name && r.name.trim().toLowerCase() === n.trim().toLowerCase()));
  return (
    <div className="se-sub" data-testid="match-list">
      <div className="se-bar"><strong>Match periods list</strong>
        <span className="se-muted">{s.stored ? `version ${s.version}` : 'the defaults'} · {canEdit ? 'admins only' : 'read-only: only admins change this'} · match days only; the first entry that matches a drill name decides</span></div>
      <div className="se-muted" style={{ marginTop: 4 }}>
        Max speed and load: his max speed and peaks on a match day come only from these. Load only: counted time, never a max speed. Neither: not counted
        (Entire Session and Entire Session - Live always count as neither). A name on no entry counts for load only and is reported as unknown.
      </div>
      {names.length > 0 && (
        <div className="se-banner" style={{ marginTop: 6 }} data-testid="match-list-unknown">
          {names.length} unknown name{names.length === 1 ? '' : 's'} in this match:{' '}
          {names.map(n => (
            <span key={n} style={{ marginLeft: 8 }}>{n}{canEdit && MATCH_CLASSES.map(([c, l]) => (
              <button key={c} className="se-btn se-btn--small" style={{ marginLeft: 4 }} onClick={() => setRows([...rows, { cls: c, label: n, name: n }])}>add as {l}</button>))}</span>
          ))}
        </div>
      )}
      <table className="se-table" style={{ marginTop: 6 }}>
        <thead><tr><th className="l">entry</th><th className="l">matches</th><th className="l">counts for</th><th /></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="l"><input className="se-input" style={{ width: 260 }} aria-label={`entry ${i + 1} label`} value={r.label} disabled={!canEdit} onChange={e => set(i, 'label', e.target.value)} /></td>
              <td className="l">
                {r.name != null
                  ? <input className="se-input" style={{ width: 200 }} aria-label={`entry ${i + 1} name`} value={r.name} disabled={!canEdit} onChange={e => set(i, 'name', e.target.value)} />
                  : <input className="se-input" style={{ width: 200, fontFamily: 'monospace' }} aria-label={`entry ${i + 1} pattern`} title="a pattern (regular expression) on the drill name in lower case, spaces around hyphens removed" value={r.pattern} disabled={!canEdit} onChange={e => set(i, 'pattern', e.target.value)} />}
                <span className="se-muted" style={{ marginLeft: 4 }}>{r.name != null ? 'name' : 'pattern'}</span>
              </td>
              <td className="l"><select className="se-input" aria-label={`entry ${i + 1} class`} value={r.cls} disabled={!canEdit} onChange={e => set(i, 'cls', e.target.value)}>
                {MATCH_CLASSES.map(([c, l]) => <option key={c} value={c}>{l}</option>)}</select></td>
              <td>{canEdit && <button className="se-btn se-btn--small" onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit && (
        <div className="se-bar" style={{ marginTop: 6 }}>
          <input className="se-input" style={{ width: 220 }} aria-label="new drill name" placeholder="drill name, e.g. Match-Fourth Half" value={add.name} onChange={e => setAdd({ ...add, name: e.target.value })} />
          <select className="se-input" aria-label="new entry class" value={add.cls} onChange={e => setAdd({ ...add, cls: e.target.value })}>{MATCH_CLASSES.map(([c, l]) => <option key={c} value={c}>{l}</option>)}</select>
          <button className="se-btn" disabled={!add.name.trim()} onClick={() => { setRows([...rows, { cls: add.cls, label: add.name.trim(), name: add.name.trim() }]); setAdd({ name: '', cls: add.cls }); }}>Add</button>
          <button className="se-btn se-btn--primary" disabled={!dirty} onClick={save}>Save list</button>
          <button className="se-btn" disabled={!dirty} onClick={() => setRows(s.entries.map(e => ({ ...e })))}>Discard</button>
          {s.stored && <button className="se-btn" onClick={reset}>Back to the defaults</button>}
        </div>
      )}
      <Err msg={err} />
    </div>
  );
}

// ── "Count this stretch" (gaps build Stage B): the selected player's excluded stretches on a match day, and the ones
// counted by hand. Saved straight away for the whole session; Remove makes the stretch excluded again. ─────────────
export function GapStretches({ label, excluded, overrides, canEdit, busy, onCount, onRemove, onGo }) {
  const dur = (a, b) => { const m = Math.round((b - a) / 1000); return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`; };
  return (
    <div className="se-sub" data-testid="gap-stretches">
      <div><strong>Match day: {label}</strong> <span className="se-muted">— time outside his counted match periods is excluded (grey).</span></div>
      <div className="se-muted" style={{ marginTop: 2 }}>
        "Count this stretch": the stretch is no longer excluded for him in this session. Hints, the trace and the squad median treat it like a counted period
        (load only). It never changes his max speed or peaks: those come only from his own halves and runs. Distance, zones and duration do not change. Saved straight away; Remove makes it excluded again.
      </div>
      <div className="se-bar" style={{ marginTop: 4, flexWrap: 'wrap' }}>
        {!excluded.length && <span className="se-muted">no excluded stretch</span>}
        {excluded.map(([a, b]) => (
          <span className="se-pill" key={`x${a}`} style={{ textTransform: 'none' }}>
            <button className="se-btn se-btn--small" style={{ border: 'none', background: 'none', padding: 0 }} onClick={() => onGo(a, b)} title="show it on the graphs">excluded {clock(a)}–{clock(b)} ({dur(a, b)})</button>
            {canEdit && <button className="se-btn se-btn--small" style={{ marginLeft: 6 }} disabled={busy} onClick={() => onCount(a, b)}>Count this stretch</button>}
          </span>
        ))}
        {overrides.map(o => (
          <span className="se-pill se-pill--est" key={`o${o.id}`} style={{ textTransform: 'none' }}>
            <button className="se-btn se-btn--small" style={{ border: 'none', background: 'none', padding: 0 }} onClick={() => onGo(toMs(o.from), toMs(o.to))} title="show it on the graphs">
              counted by you {clock(toMs(o.from))}–{clock(toMs(o.to))}{o.source === 'hint' ? ' (from the movement hint)' : ''}
            </button>
            {canEdit && <button className="se-btn se-btn--small" style={{ marginLeft: 6 }} disabled={busy} onClick={() => onRemove(o)}>Remove</button>}
          </span>
        ))}
      </div>
    </div>
  );
}

export function HistoryPanel({ history, labelOf, canUndo, onUndo }) {
  if (!history) return <div className="se-sub">…</div>;
  return (
    <div className="se-sub">
      <strong>Hint history</strong>
      {!history.length && <div className="se-muted">No accepted or dismissed hints yet.</div>}
      <table className="se-table" style={{ marginTop: 6 }}>
        <tbody>
          {history.map(d => (
            <tr key={d.id} className={d.undoneAt ? 'se-out' : ''}>
              <td className="l">{clock(toMs(d.decidedAt))}</td>
              <td className="l">{labelOf(d.sessionPlayerId)}</td>
              <td className="l">{d.signal.replace(/_/g, ' ')}</td>
              <td className="l">{d.decision}{d.batchId ? ' (accept all)' : ''}{d.undoneAt ? ' · undone' : ''}</td>
              <td className="l se-muted">{d.suggestion.at ? clock(toMs(d.suggestion.at)) : d.suggestion.action}</td>
              <td className="l se-muted" style={{ whiteSpace: 'normal' }}>{Object.entries(d.values || {}).map(([k, v]) => `${k} ${v}`).join(', ')}</td>
              <td>{d.decision === 'accepted' && !d.undoneAt && <button className="se-btn se-btn--small" disabled={!canUndo} title={canUndo ? 'undo this accept' : 'save or cancel your changes first'} onClick={() => onUndo(d)}>Undo</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Keyboard shortcuts (§7c) ─────────────────────────────────────────────────────────────────────────────────────
export function KeysPanel() {
  const rows = [['[ ]', 'set start / end at the mouse position (either graph)'], ['← →', 'nudge the active boundary 1 s (Shift: 5 s)'], ['↑ ↓', 'previous / next player'],
    ['N', 'next player with a hint'], ['A', "accept the selected player's hints"], ['X', 'selected player in / out of the period'], ['Z · R', 'zoom out · reset zoom'],
    ['M', 'note at the mouse position'], ['S · Ctrl+S', 'save'], ['?', 'show / hide this list'], ['Esc', 'cancel a zoom box, close panels']];
  return (
    <div className="se-sub">
      <strong>Keyboard shortcuts</strong>
      <table className="se-keys"><tbody>{rows.map(([k, t]) => <tr key={k}><td>{k.split(' ').map(x => <kbd key={x} style={{ marginRight: 3 }}>{x}</kbd>)}</td><td>{t}</td></tr>)}</tbody></table>
      <span className="se-muted">Keys do nothing while you type in a field. The active boundary has a blue outline.</span>
    </div>
  );
}
